import { useRef, useState, type ReactNode } from 'react'

import { ADDRESS_KIND_VALUES, UF_VALUES } from '@aeris/contracts'
import { isValidCepFormat, normalizeCep } from '@aeris/party'
import { Input, Select } from '@aeris/ui'

import { ufOrNull, type AddressValues } from '../lib/address-values.ts'
import { lookupCep, mergeCepInto, type CepAddress } from '../lib/cep.ts'
import { ADDRESS_KIND_LABELS } from '../lib/party-labels.ts'

/**
 * Os campos de um endereço brasileiro, com a busca de CEP — F089, F130.
 *
 * Moram aqui, e não dentro da ficha, porque agora são usados em dois lugares:
 * no endereço adicionado a um cadastro que já existe e no "Novo cadastro", onde
 * a busca de CNPJ preenche o endereço antes de salvar.
 *
 * Campos controlados, sem um segundo react-hook-form: eles vivem dentro do
 * formulário da pessoa, e um `<form>` dentro de outro é HTML inválido.
 *
 * ## O CEP busca, e a razão original continua valendo
 *
 * O bloco original dizia *"No CEP lookup: an address form that cannot be
 * completed while the network is down is one that stops a sale"*. Continua
 * verdade — a seção 25 faz de offline um estado suportado. A busca não valida,
 * não trava e não é etapa: preenche o que está vazio e some.
 *
 * **Nunca sobrescreve o que a pessoa digitou.** Um campo só é preenchido se
 * estiver vazio ou se ainda tiver o valor que a consulta ANTERIOR pôs nele.
 */

export const AddressInputs = ({
  values,
  onChange,
}: {
  values: AddressValues
  onChange: (update: (current: AddressValues) => AddressValues) => void
}): ReactNode => {
  const set = <K extends keyof AddressValues>(
    key: K,
    value: AddressValues[K]
  ): void => {
    onChange((current) => ({ ...current, [key]: value }))
  }

  const [lookupState, setLookupState] = useState<'idle' | 'busy' | 'missed'>(
    'idle'
  )
  /** O que a última busca preencheu — o que ela pode sobrescrever na próxima. */
  const lastLookup = useRef<CepAddress | null>(null)
  /** O único campo que a busca nunca sabe. Recebe o foco quando o resto chega. */
  const numberRef = useRef<HTMLInputElement>(null)

  const searchCep = (raw: string): void => {
    const digits = normalizeCep(raw)
    if (!isValidCepFormat(digits)) {
      setLookupState('idle')
      return
    }

    setLookupState('busy')
    void lookupCep(digits).then((address) => {
      if (address === null) {
        setLookupState('missed')
        return
      }

      setLookupState('idle')
      onChange((current) => {
        const merged = mergeCepInto(
          {
            street: current.street,
            district: current.district,
            city: current.city,
            uf: current.uf,
            ibgeCode: current.ibgeCode,
          },
          address,
          lastLookup.current
        )

        return {
          ...current,
          street: merged.street,
          district: merged.district,
          city: merged.city,
          ibgeCode: merged.ibgeCode,
          /*
           * A UF é um `Select` de conjunto fechado, e o que volta do serviço é
           * uma string qualquer. Uma sigla fora da lista viraria um valor
           * selecionado que não existe nela — então só entra conferida.
           */
          uf: ufOrNull(merged.uf) ?? current.uf,
        }
      })
      lastLookup.current = address
      numberRef.current?.focus()
    })
  }

  return (
    <>
      <div className="grid gap-2 sm:grid-cols-4">
        <Input
          value={values.cep}
          onChange={(event) => {
            set('cep', event.target.value)
            /*
             * Ao COMPLETAR os oito dígitos, e não a cada tecla: o aviso do
             * ViaCEP é sobre uso massivo. No `blur` também, para quem cola o
             * CEP de um jeito que não passa pelos oito dígitos no caminho.
             */
            searchCep(event.target.value)
          }}
          onBlur={(event) => {
            searchCep(event.target.value)
          }}
          placeholder="CEP"
          aria-label="CEP"
          className="font-(--font-mono)"
        />
        <Input
          value={values.street}
          onChange={(event) => {
            set('street', event.target.value)
          }}
          placeholder="Logradouro"
          aria-label="Logradouro"
          className="sm:col-span-2"
        />
        <Input
          ref={numberRef}
          value={values.number}
          onChange={(event) => {
            set('number', event.target.value)
          }}
          placeholder="Número"
          aria-label="Número"
        />
        <Input
          value={values.complement}
          onChange={(event) => {
            set('complement', event.target.value)
          }}
          placeholder="Complemento"
          aria-label="Complemento"
        />
        <Input
          value={values.district}
          onChange={(event) => {
            set('district', event.target.value)
          }}
          placeholder="Bairro"
          aria-label="Bairro"
        />
        <Input
          value={values.city}
          onChange={(event) => {
            set('city', event.target.value)
          }}
          placeholder="Município"
          aria-label="Município"
        />
        <Select
          value={values.uf}
          onChange={(event) => {
            set('uf', event.target.value as AddressValues['uf'])
          }}
          aria-label="UF"
        >
          {UF_VALUES.map((uf) => (
            <option key={uf} value={uf}>
              {uf}
            </option>
          ))}
        </Select>
        <Input
          value={values.ibgeCode}
          onChange={(event) => {
            set('ibgeCode', event.target.value)
          }}
          placeholder="Código IBGE"
          aria-label="Código IBGE do município"
          className="font-(--font-mono)"
        />
        <Select
          value={values.kind}
          onChange={(event) => {
            set('kind', event.target.value as AddressValues['kind'])
          }}
          aria-label="Tipo de endereço"
        >
          {ADDRESS_KIND_VALUES.map((kind) => (
            <option key={kind} value={kind}>
              {ADDRESS_KIND_LABELS[kind]}
            </option>
          ))}
        </Select>
      </div>

      {/*
       * O estado da busca, numa linha só e sem `role="alert"`: "não achei" NÃO
       * é erro. O endereço continua digitável e o salvar continua ativo.
       */}
      {lookupState === 'idle' ? null : (
        <p className="text-(length:--text-xs) text-(--text-muted)">
          {lookupState === 'busy'
            ? 'Buscando o endereço deste CEP…'
            : 'Não achei este CEP. Preencha à mão — dá no mesmo.'}
        </p>
      )}
    </>
  )
}
